<template>
  <section>
    <SubLinks />
    <div
      class="flex flex-wrap items-start justify-between sm:flex-nowrap md:justify-start md:space-x-12"
    >
      <div v-if="ui.loaded" class="mt-4 lg:w-32">
        <div
          v-for="showtime in showtimes"
          :key="showtime.id"
          :id="`showtime-${showtime.id}`"
          class="font-sans text-fg-link-primary cursor-pointer mb-3"
          @click="selectShowTime(showtime)"
          :class="[activeClass((currentShowTime && currentShowTime.id) === showtime.id)]"
        >
          {{ showtime.name }}
        </div>
        <ButtonDefault label="Add" id="add-showtime-btn" @click="newShowTime()" class="px-8" alt />
      </div>
      <div v-else>
        <Spinner />
      </div>

      <template v-if="currentShowTime">
        <div
          class="pt-5 mt-5 order-last border-t border-solid border-linear-subtle w-full sm:order-1 sm:w-1/2 sm:pt-0 sm:mt-0 sm:border-t-0 sm:border-r sm:pr-10 md:w-[500px] md:pr-12"
        >
          <ErrorDisplay v-bind="ui.error" />
          <div class="font-label text-fg-secondary text-xs uppercase mb-1">Name</div>
          <div class="border border-linear-subtle rounded mb-2">
            <input
              class="pl-2 px-4 font-sans text-base leading-8 w-full bg-surface-primary rounded-sm"
              id="showtime-name-input"
              v-model="currentShowTime.name"
            />
          </div>
          <div class="font-label text-fg-secondary text-xs uppercase mb-1">Showtime</div>
          <div class="border border-linear-subtle rounded">
            <input
              type="time"
              class="pl-2 px-4 font-sans text-base leading-8 w-full bg-surface-primary rounded-sm"
              id="showtime-time-input"
              v-model="currentShowTime.time"
            />
          </div>
          <SeatingTimeFormList
            class="w-full"
            :seatingTimes="seatingTimeForms"
            @delete="seatingTimeForms.splice($event, 1)"
            :deleteAlwaysAllowed="true"
          />
          <div class="flex space-x-4 justify-end items-baseline pb-5 mt-10">
            <ButtonDefault
              label="Delete"
              id="delete-showtime-btn"
              @click="setState(STATE.DELETE_SHOWTIME)"
              v-show="canDeleteShowTime"
              :disabled="ui.saving"
              class="w-24 px-8"
              alt
            />
            <ButtonDefault
              :label="ui.saving ? 'Saving...' : 'Save'"
              id="showtime-form-save-btn"
              classes="w-24"
              @click="saveForm()"
              :disabled="ui.saving"
            />
          </div>
        </div>
        <div class="flex flex-col justify-between h-full text-right sm:order-last md:text-left">
          <div class="font-label text-fg-secondary text-xs uppercase">Total Potential Guests</div>
          <div class="font-heading font-semibold text-4xl">{{ possibleGuests }}</div>
        </div>
      </template>
    </div>
    <SlotModal :display="deleteShowTimeModal" key="delShowTime" @close="setState(STATE.INITIAL)">
      <template v-if="state == STATE.DELETE_SHOWTIME || state == STATE.DELETED_SHOWTIME" #header>
        <div v-if="state == STATE.DELETE_SHOWTIME">Please Confirm</div>
        <div v-else>Success</div>
      </template>
      <template v-if="state == STATE.DELETE_SHOWTIME">
        <template v-if="currentShowTime">
          <div id="delete-showtime-confirm-text" class="mb-8">
            Are you sure you want to delete the
            <span class="font-semibold">{{ currentShowTime.name }}</span> Show Time?
          </div>
        </template>
        <ErrorDisplay v-bind="ui.error" />
        <div class="mb-2 flex justify-between">
          <ButtonDefault label="Cancel" alt @click="setState(STATE.INITIAL)" />
          <ButtonDefault
            id="delete-showtime-confirm-btn"
            label="Delete"
            @click="deleteShowTime"
            altWarning
          />
        </div>
      </template>
      <template v-if="state == STATE.DELETING_SHOWTIME">
        <div class="flex flex-col">
          <div class="mb-8 text-center">Deleting...</div>
          <LoaderLine />
        </div>
      </template>
      <template v-if="state == STATE.DELETED_SHOWTIME">
        <template v-if="currentShowTime">
          <div class="mb-8">
            <span class="font-semibold">{{ currentShowTime.name }}</span> has been deleted.
          </div>
          <div class="text-center">
            <ButtonDefault
              label="Done"
              @click=";(currentShowTime = null), setState(STATE.INITIAL)"
            />
          </div>
        </template>
      </template>
    </SlotModal>
  </section>
</template>

<script lang="ts">
import { mapStores } from 'pinia'
import { defineComponent } from 'vue'

import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import ErrorDisplay from '@/common/components/atoms/ErrorDisplay.vue'
import LoaderLine from '@/common/components/atoms/LoaderLine.vue'
import Spinner from '@/common/components/atoms/Spinner.vue'
import SubLinks from '@/common/components/molecules/SubLinks.vue'
import SlotModal from '@/common/components/organisms/SlotModal.vue'
import SeatingTimeFormList from '@/components/seatingtimes/SeatingTimeFormList.vue'
import { FormSeatingTime, StoreShowTime } from '@/models'
import { useSettingStore } from '@/stores/setting'
import { useShowTimeStore } from '@/stores/showtime'
import { blankError, clearError, displayHandled } from '@/utils/errors'
import { activeClass, sum } from '@/utils/utils'

import { ShowTimeWrite } from '@generated/types'

const STATE = {
  INITIAL: 'initial',
  DELETE_SHOWTIME: 'delete-showtime',
  DELETING_SHOWTIME: 'deleting-showtime',
  DELETING_SHOWTIME_ERROR: 'deleting-showtime-error',
  DELETED_SHOWTIME: 'deleted-showtime',
}

export default defineComponent({
  name: 'ShowTimes',
  components: {
    SlotModal,
    SubLinks,
    ButtonDefault,
    SeatingTimeFormList,
    ErrorDisplay,
    Spinner,
    LoaderLine,
  },
  data() {
    return {
      currentShowTime: null as ShowTimeWrite | null,
      seatingTimeForms: [] as FormSeatingTime[],
      state: STATE.INITIAL,
      ui: {
        error: blankError(),
        saving: false,
        loaded: false,
      },
      STATE,
    }
  },

  computed: {
    ...mapStores(useSettingStore, useShowTimeStore),

    canDeleteShowTime(): boolean {
      return !!this.currentShowTime?.id
    },

    deleteShowTimeModal(): boolean {
      const state = this.state
      return (
        state == this.STATE.DELETE_SHOWTIME ||
        state == this.STATE.DELETED_SHOWTIME ||
        state == this.STATE.DELETING_SHOWTIME
      )
    },

    showtimes(): StoreShowTime[] {
      return this.showtimeStore.showtimes || []
    },

    possibleGuests(): number {
      return sum(this.seatingTimeForms.map(st => Number(st.maximum_guests)))
    },

    processedShowTimeWrite(): ShowTimeWrite {
      return {
        ...this.currentShowTime,
        time: this.settingStore.makeDateTime({ time: this.currentShowTime?.time }),
        seating_times: this.seatingTimeForms.map(st => ({
          id: st['id'] || undefined,
          time: this.settingStore.makeDateTime({ time: st.time24 }),
          maximum_guests: st.maximum_guests,
        })),
      }
    },
  },

  async created() {
    await this.showtimeStore.fetchAll()
    this.ui.loaded = true
  },

  methods: {
    async deleteShowTime() {
      this.setState(STATE.DELETING_SHOWTIME)
      try {
        await this.showtimeStore.deleteShowTime(Number(this.currentShowTime?.id))
        setTimeout(() => this.setState(STATE.DELETED_SHOWTIME), 1000)
      } catch (error) {
        this.setState(STATE.DELETING_SHOWTIME_ERROR, false)
        displayHandled(error, this.ui.error)
      }
    },

    newShowTime() {
      this.currentShowTime = {
        name: '',
        time: '19:00',
      }
      this.seatingTimeForms = []
    },

    selectShowTime(showtime: StoreShowTime) {
      this.currentShowTime = {
        id: showtime.id || 0,
        name: showtime.name || '',
        time: this.settingStore.time24Str(showtime.time),
        seating_times: [],
      }
      this.seatingTimeForms = (showtime.seating_times || []).map(st => ({
        time24: st.time24,
        maximum_guests: st.maximum_guests,
      }))
    },

    setState(state, clearErr?: boolean) {
      if (clearErr) clearError(this.ui.error)
      this.state = state
    },

    async saveForm() {
      if (this.ui.saving) return
      if (!this.currentShowTime) {
        throw new Error('Attempted to saveForm, but this.currentShowTime was undefined')
      }
      try {
        this.ui.saving = true
        clearError(this.ui.error)
        const form = this.processedShowTimeWrite
        if (form.id) {
          await this.showtimeStore.updateShowTime(form)
        } else {
          const showtime = await this.showtimeStore.addShowTime(form)
          this.selectShowTime(showtime)
        }
      } catch (error) {
        displayHandled(error, this.ui.error)
      } finally {
        this.ui.saving = false
      }
    },
    activeClass,
  },
})
</script>
